import { FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import PageHeaderIconButton from "../../../Layout/Header/PageHeaderIconButton";
import PageHeaderButton from "../../../Layout/Header/PageHeaderButton";
import PageHeader from "../../../Layout/Header/PageHeader";
import { MdmTabs } from "../../../Layout/Tabs/MdmTabs";
import { Spdr } from "../Spdr";
import { spdrDefaultValues } from "./spdrDefaultValues";
import { confirm } from "../../../Layout/Dialog/ConfirmDialog";
import { SpdrInformationForm } from "./SpdrInformationForm";
import { SpdrInboundForm } from "./SpdrInboundForm";
import { SpdrReturnForm } from "./SpdrReturnForm";
import { spdrSchema } from "./spdrSchema";
import { isFormSubmitForbidden } from "../../../Forms/isFormSubmitForbidden";
import { resetFormStateAndKeepFormValues } from "../../../Forms/resetFormStateAndKeepFormValues";
import { useIsUserInRoles } from "../../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";

type Props = {
  title: string;
  spdr: Spdr;
  onSave: (spdr: Spdr) => void;
  onDelete?: (spdrId: number | null | undefined) => void;
  disabledFields?: Array<string>;
};
export const SpdrFormWithTabs = ({ title, spdr, onSave, onDelete, disabledFields }: Props) => {
  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);

  const methods = useForm<Spdr>({
    defaultValues: spdrDefaultValues,
    resolver: yupResolver(spdrSchema),
    mode: "all",
    disabled: !isUserInAdminNetworkRole,
  });

  useEffect(() => {
    methods.reset(spdr);
    return () => methods.reset(spdrDefaultValues);
  }, [spdr, methods, methods.reset]);

  const onDeleteWithConfirmation = async () => {
    const isConfirmed = await confirm(`Do you really want to delete SPDR with Id ${spdr?.Id}?`);
    if (!isConfirmed || !onDelete) {
      return;
    }
    await onDelete(spdr.Id);
  };

  return (
    <>
      <PageHeader headerText={title}>
        {onDelete !== undefined && (
          <PageHeaderIconButton
            onClick={() => onDeleteWithConfirmation()}
            color="secondary"
            disabled={!isUserInAdminNetworkRole}
          >
            <DeleteIcon />
          </PageHeaderIconButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((currentValues: Spdr) => {
            onSave(currentValues);
            resetFormStateAndKeepFormValues(methods);
          })}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          Save
        </PageHeaderButton>
      </PageHeader>

      <FormProvider {...methods}>
        <MdmTabs
          tabs={[
            {
              index: 0,
              label: "INFORMATION",
              content: <SpdrInformationForm disabledFields={disabledFields} />,
            },
            {
              index: 1,
              label: "INBOUND",
              content: <SpdrInboundForm disabledFields={disabledFields} />,
              disabled: !spdr,
            },
            {
              index: 2,
              label: "RETURN",
              content: <SpdrReturnForm disabledFields={disabledFields} />,
              disabled: !spdr,
            },
          ]}
        />
      </FormProvider>
    </>
  );
};
